// Small utility helpers for money, dates, durations, names.

export const formatMoney = (cents, currency = 'GHS') => {
  const n = Number(cents) || 0;
  return new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(n);
};

// Currency symbol prefix used by formatMoneyCompact. Kept locally so we
// don't have to round-trip through Intl just to pull the symbol out.
const CURRENCY_SYMBOL = {
  GHS: 'GH₵',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

// Compact currency for narrow KPI cards. ALWAYS produces a short string
// (≤7 characters for typical values), regardless of magnitude:
//
//   0          → GH₵0
//   847        → GH₵847
//   1,234      → GH₵1.2K
//   12,345     → GH₵12K
//   123,456    → GH₵123K
//   999,999    → GH₵1M     (escalated — would otherwise round to "1000K")
//   1,234,567  → GH₵1.2M
//   12,345,678 → GH₵12M
//
// The first version of this used `Intl.NumberFormat({ notation: 'compact' })`,
// but the en-GH locale's support for compact currency formatting is
// inconsistent across browsers — some return the long form anyway —
// which led to KPI cards showing "GH₵..." (truncated by CSS). Doing the
// scaling manually guarantees the string is short enough to fit, while
// the original formatMoney() stays available for tooltips and tables
// where the full precision matters.
// Smallest-to-largest order. Critical for the escalation logic below:
// when 999,500 rounds to 1000K at the K tier we want to fall through
// and emit "1M" rather than the long "GH₵999,500".
const COMPACT_TIERS = [
  { mag: 1e3,  suffix: 'K' },
  { mag: 1e6,  suffix: 'M' },
  { mag: 1e9,  suffix: 'B' },
  { mag: 1e12, suffix: 'T' },
];

export const formatMoneyCompact = (cents, currency = 'GHS') => {
  const n   = Number(cents) || 0;
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  const sign = n < 0 ? '-' : '';
  const abs  = Math.abs(n);

  // Values below 1K render with their full digits — short enough to
  // fit a narrow card on their own ("GH₵847" is 7 chars).
  if (abs < 1000) {
    return `${sign}${sym}${Math.round(abs).toLocaleString('en-US')}`;
  }

  // Walk tiers smallest-to-largest. At each tier we compute the scaled
  // value and round. If rounding pushes the integer part to ≥ 1000
  // (e.g. 999.9 at the K tier), the result would be "1000K" — ugly and
  // five characters too wide. Skip to the next tier so the larger
  // suffix handles it cleanly ("1M").
  for (const { mag, suffix } of COMPACT_TIERS) {
    const scaled = abs / mag;
    // 1 decimal when scaled < 10 (e.g. "1.2K"); otherwise integer
    // ("12K", "123K"). Keeps the output narrow and the precision useful.
    const rounded = scaled < 10
      ? Math.round(scaled * 10) / 10
      : Math.round(scaled);
    if (rounded < 1)      continue; // not yet at this tier
    if (rounded >= 1000)  continue; // would overflow — escalate
    const text = scaled < 10
      ? rounded.toFixed(1).replace(/\.0$/, '')
      : rounded.toString();
    return `${sign}${sym}${text}${suffix}`;
  }
  // Values past quadrillion just print fully — astronomical anyway.
  return `${sign}${sym}${Math.round(abs).toLocaleString('en-US')}`;
};

export const formatNumber = (n, opts = {}) => {
  const { maximumFractionDigits = 0 } = opts;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(Number(n) || 0);
};

export const formatDate = (d, opts = {}) => {
  if (!d) return '';
  const { style = 'medium', includeTime = false } = opts;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const fmt = {
    short:  { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long:   { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    iso:    null,
  }[style];
  if (style === 'iso') return date.toISOString().slice(0, 10);
  const base = date.toLocaleDateString('en-US', fmt);
  if (!includeTime) return base;
  return `${base} · ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

export const formatRelative = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const diffMs = date.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const day = 86400000;
  const sign = diffMs < 0 ? 'ago' : 'from now';
  if (abs < 60000) return diffMs < 0 ? 'just now' : 'in a moment';
  if (abs < 3600000) return `${Math.round(abs / 60000)}m ${sign}`;
  if (abs < day) return `${Math.round(abs / 3600000)}h ${sign}`;
  if (abs < day * 7) return `${Math.round(abs / day)}d ${sign}`;
  return formatDate(date);
};

export const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

export const fullName = (person) => {
  if (!person) return '';
  if (person.organization && !person.first_name) return person.organization;
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || person.organization || '';
};

export const initials = (person) => {
  if (!person) return '??';
  const name = fullName(person);
  return name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('') || '??';
};

export const classNames = (...parts) => parts.filter(Boolean).join(' ');
